/**
 * Card Generator Utility
 * يقوم بإنشاء بطاقة المستخدم بناءً على البيانات المدخلة
 */

/**
 * تحميل صورة
 * @param {string} src - مسار الصورة
 * @returns {Promise<HTMLImageElement>}
 */
const loadImage = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
};

/**
 * اختيار الصورة الافتراضية حسب الجنس
 * @param {number} gender - جنس المستخدم (1 = ذكر، 0 = أنثى)
 * @returns {string} - مسار الصورة الافتراضية
 */
const getDefaultAvatarByGender = (gender) => {
  // تحويل الجنس إلى رقم صحيح للتأكد
  const genderNum = parseInt(gender);
  
  if (genderNum === 1) {
    return '/default-male-avatar.jpg'; // ذكر (JPG)
  } else if (genderNum === 0) {
    return '/default-female-avatar.png'; // أنثى (PNG)
  } else {
    // إذا لم يكن محدداً، استخدم صورة الذكر كافتراضي
    return '/default-male-avatar.jpg';
  }
};

/**
 * رسم نص متعدد الأسطر مع التفاف تلقائي ومحاذاة في الوسط
 * @param {CanvasRenderingContext2D} ctx - سياق الرسم
 * @param {string} text - النص المراد رسمه
 * @param {number} x - إحداثي X (المركز)
 * @param {number} y - إحداثي Y
 * @param {number} maxWidth - العرض الأقصى للنص
 * @param {number} lineHeight - ارتفاع السطر
 * @returns {number} - عدد الأسطر
 */
const wrapText = (ctx, text, x, y, maxWidth, lineHeight) => {
  const words = text.split(' ');
  let line = '';
  let lineCount = 0;
  let currentY = y;
  const lines = [];

  // تقسيم النص إلى أسطر
  for (let n = 0; n < words.length; n++) {
    const testLine = line + words[n] + ' ';
    const metrics = ctx.measureText(testLine);
    const testWidth = metrics.width;

    if (testWidth > maxWidth && n > 0) {
      lines.push(line.trim());
      line = words[n] + ' ';
      lineCount++;
    } else {
      line = testLine;
    }
  }
  lines.push(line.trim());
  lineCount++;
 
  // رسم كل سطر في الوسط
  lines.forEach((line, index) => {
    ctx.fillText(line, x, currentY + (index * lineHeight));
  });

  return lineCount;
};

const adjustFontSizeToFit = (
  ctx,
  text,
  {
    baseSize = 42,
    minSize = 34,
    maxWidth = 680,
    fontBuilder,
  } = {}
) => {
  if (typeof fontBuilder !== 'function') {
    return baseSize;
  }

  let fontSize = baseSize;
  while (fontSize > minSize) {
    ctx.font = fontBuilder(fontSize);
    const width = ctx.measureText(text).width;
    if (width <= maxWidth) {
      break;
    }
    fontSize -= 2;
  }

  return fontSize;
};

const formatIdWithSpacing = (value = '') => {
  if (!value) {
    return '';
  }
  return value
    .toString()
    .split('')
    .join(' ')
    .replace(/\s{2,}/g, ' ');
};

/**
 * رسم صورة دائرية
 * @param {CanvasRenderingContext2D} ctx - سياق الرسم
 * @param {HTMLImageElement} image - الصورة المراد رسمها
 * @param {number} x - إحداثي X للمركز
 * @param {number} y - إحداثي Y للمركز
 * @param {number} radius - نصف قطر الدائرة
 */
const drawCircularImage = (ctx, image, x, y, radius) => {
  ctx.save();
  
  // إنشاء قناع دائري
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.closePath();
  ctx.clip();

  // حساب أبعاد الصورة لملء الدائرة بالكامل (cover mode)
  const scale = Math.max(
    (radius * 2) / image.width,
    (radius * 2) / image.height
  );
  
  const drawWidth = image.width * scale;
  const drawHeight = image.height * scale;
  const drawX = x - drawWidth / 2;
  const drawY = y - drawHeight / 2;

  // رسم الصورة لتملأ الدائرة بالكامل
  ctx.drawImage(image, drawX, drawY, drawWidth, drawHeight);
  
  ctx.restore();
};

const loadFontFace = async (name, urls, descriptors) => {
  if (document.fonts.check(`12px "${name}"`)) {
    return;
  }

  for (const url of urls) {
    try {
      const fontFace = new FontFace(name, `url(${url})`, descriptors);
      await fontFace.load();
      document.fonts.add(fontFace);
      console.log(`✅ تم تحميل خط ${name} من: ${url}`);
      return;
    } catch (error) {
      continue;
    }
  }

  console.warn(`⚠️ تعذر تحميل الخط ${name} من أي مسار.`);
};

/**
 * تحميل خطوط Noor (العادي والعريض)
 */
const loadNoorFont = async () => {
  try {
    await loadFontFace('Noor', ['/noor.ttf', '/noor.otf', '/Noor.ttf', '/Noor.otf'], {
      weight: 'normal',
      style: 'normal',
    });
    await loadFontFace(
      'Noor Bold',
      [
        '/noor-bold.ttf',
        '/noor-bold.otf',
        '/Noor-Bold.ttf',
        '/Noor-Bold.otf',
        '/NoorBold.ttf',
        '/NoorBold.otf',
      ],
      {
        weight: 'bold',
        style: 'normal',
      }
    );
  } catch (error) {
    console.warn('فشل تحميل خطوط Noor:', error);
  }
};

const loadNotoSerifFont = async () => {
  try {
    await loadFontFace(
      'Noto Serif Condensed',
      [
        '/noto-serif-condensed.ttf',
        '/NotoSerifCondensed.ttf',
        '/NotoSerif-Condensed.ttf',
        '/NotoSerifCondensed-ExtraLight.ttf',
      ],
      {
        weight: '200',
        style: 'normal',
      }
    );
  } catch (error) {
    console.warn('فشل تحميل خط Noto Serif Condensed:', error);
  }
};

/**
 * توليد بطاقة المستخدم
 * @param {Object} userData - بيانات المستخدم
 * @param {string} userData.userName - اسم المستخدم
 * @param {string} userData.schoolName - اسم المدرسة
 * @param {string} userData.userIdNumber - رقم البطاقة
 * @param {string} userData.userImage - مسار صورة المستخدم (اختياري)
 * @param {number} userData.userGender - جنس المستخدم (1 = ذكر، 0 = أنثى، null/undefined = غير محدد)
 * @param {string} userData.expiryDate - تاريخ انتهاء الصلاحية (اختياري)
 * @returns {Promise<HTMLCanvasElement>}
 */
export const generateCard = async (userData) => {
  const {
    userName = 'اسم المستخدم',
    schoolName = 'اسم المدرسة',
    userIdNumber = 'XXXXX',
    userImage = null,
    userGender = null,
    expiryDate = null,
  } = userData;
  // النساء يجب أن يظهرن دائماً بالصورة الافتراضية
  const shouldForceFemaleAvatar = parseInt(userGender, 10) === 0;

  // تحميل الخطوط المطلوبة
  await loadNoorFont();
  await loadNotoSerifFont();

  // إنشاء Canvas
  const canvas = document.createElement('canvas');
  canvas.width = 780;
  canvas.height = 1170;
  const ctx = canvas.getContext('2d');

  try {
    // تحميل صورة البطاقة الفارغة (PNG أو JPG)
    let templateImage;
    try {
      templateImage = await loadImage('/card-template.png');
    } catch (error) {
      // إذا لم يتم العثور على PNG، حاول JPG
      templateImage = await loadImage('/card-template.jpg');
    }
    
    // رسم صورة البطاقة الفارغة
    ctx.drawImage(templateImage, 0, 0, canvas.width, canvas.height);

    // إعدادات الخط والمحاذاة للنصوص العربية
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.direction = 'rtl';

    // إخفاء أي إطار رصاصي موجود في القالب بدائرة بيضاء أكبر قليلاً
    ctx.fillStyle = '#FFFFFF';
    ctx.beginPath();
    ctx.arc(390, 475, 160, 0, Math.PI * 2); // زيادة النصف القطر من 135 إلى 145
    ctx.fill();
    ctx.closePath();

    // رسم صورة المستخدم (أو الصورة الافتراضية حسب الجنس)
    try {
      let userImg;
      if (!shouldForceFemaleAvatar && userImage) {
        try {
          userImg = await loadImage(userImage);
        } catch (error) {
          console.warn('تعذر تحميل صورة المستخدم، استخدام الصورة الافتراضية:', error);
          // استخدام الصورة الافتراضية حسب الجنس
          userImg = await loadImage(getDefaultAvatarByGender(userGender));
        }
      } else {
        // استخدام الصورة الافتراضية حسب الجنس إذا لم تكن صورة المستخدم متوفرة
        userImg = await loadImage(getDefaultAvatarByGender(userGender));
      }
      
      // رسم صورة دائرية في موضع الدائرة البرتقالية
      // تحريك الصورة للأعلى قليلاً لتبدأ من أسفل الإطار
      drawCircularImage(ctx, userImg, 390, 480, 160); // تحريك Y من 475 إلى 420 لمحاذاة الأسفل
    } catch (error) {
      console.warn('تعذر تحميل الصورة (حتى الافتراضية):', error);
      // الدائرة ستبقى فارغة
    }

    // رسم اسم المستخدم (بخط كبير وعريض باللون الأبيض - خط Noor Bold)
    ctx.fillStyle = '#FFFFFF';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
    ctx.shadowBlur = 5;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;

    const buildNameFont = (size) =>
      `bold ${size}px "Noor Bold", "Noor", "Traditional Arabic", "Arial", sans-serif`;
    const fittedNameFontSize = adjustFontSizeToFit(ctx, userName, {
      baseSize: 42,
      minSize: 32,
      maxWidth: 640,
      fontBuilder: buildNameFont,
    });
    ctx.font = buildNameFont(fittedNameFontSize);
    const nameLineHeight = Math.round(fittedNameFontSize * 1.25);
    
    // رسم الاسم مع إمكانية التفاف النص
    // الاسم يظهر في المنطقة الخضراء السفلية - تم رفعه للأعلى - مساحة أوسع
    const nameY = 790;
    wrapText(ctx, userName, 390, nameY, 680, nameLineHeight);

    // رسم اسم المدرسة (باللون البرتقالي - خط Noor Bold) - تم رفعه للأعلى
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.fillStyle = '#F68F33';
    ctx.font = 'bold 30px "Noor Bold", "Noor", "Traditional Arabic", "Arial", sans-serif';
    const schoolY = 865;
    wrapText(ctx, schoolName, 390, schoolY, 680, 40);

    // رسم رقم البطاقة (في المستطيل الرمادي - خط Noor)
    // الرقم يظهر في المستطيل الرمادي الأزرق - في الوسط تماماً
    ctx.fillStyle = '#2D5F6E';
    ctx.font = '42px "Noto Serif Condensed", "Noor", serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle'; // محاذاة في الوسط عمودياً وأفقياً
    const spacedId = formatIdWithSpacing(userIdNumber);
    ctx.fillText(spacedId, 390, 967);

    // رسم تاريخ انتهاء الصلاحية (أسفل البطاقة - بخط صغير)
    if (expiryDate) {
      ctx.fillStyle = '#FFFFFF';
      ctx.font = '26px "Noor", "Arial", sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.letterSpacing = '0px';
      ctx.fillText(`نافذ لغاية تاريخ ${expiryDate}`, 390, 1095);
    }

  } catch (error) {
    console.error('خطأ في توليد البطاقة:', error);
    throw error;
  }

  return canvas;
};

/**
 * تنزيل البطاقة كصورة PNG
 * @param {HTMLCanvasElement} canvas - عنصر Canvas
 * @param {string} fileName - اسم الملف (بدون امتداد)
 */
export const downloadCard = (canvas, fileName = 'card') => {
  const link = document.createElement('a');
  link.download = `${fileName}.png`;
  link.href = canvas.toDataURL('image/png');
  link.click();
};

/**
 * توليد وتنزيل البطاقة
 * @param {Object} userData - بيانات المستخدم
 * @param {string} fileName - اسم الملف (بدون امتداد)
 */
export const generateAndDownloadCard = async (userData, fileName) => {
  try {
    const canvas = await generateCard(userData);
    downloadCard(canvas, fileName);
    return true;
  } catch (error) {
    console.error('خطأ في توليد وتنزيل البطاقة:', error);
    throw error;
  }
};

/**
 * توليد بطاقة وإرجاعها كـ Blob (للاستخدام في ZIP)
 * @param {Object} userData - بيانات المستخدم
 * @returns {Promise<Blob>}
 */
export const generateCardAsBlob = async (userData) => {
  try {
    const canvas = await generateCard(userData);
    return new Promise((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('فشل تحويل Canvas إلى Blob'));
        }
      }, 'image/png');
    });
  } catch (error) {
    console.error('خطأ في توليد البطاقة كـ Blob:', error);
    throw error;
  }
};

